<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  fetchMarketplaceJson,
  postMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';
import { formatCurrency } from '../helpers/orderHelpers';
import { useAdsSpeech } from '../composables/useAdsSpeech';

const route = useRoute();
const router = useRouter();

const { isSpeaking, isLoadingAudio, toggleSpeaking, stopSpeaking } =
  useAdsSpeech();

const ACCOUNT_LABELS = {
  koza: 'Koza Butik Reklamları',
  modax: 'ModaXlarge.com Reklam Hesabı',
  modax_shopify: 'ModaXLarge (Shopify)',
};

const isLoading = ref(true);
const loadError = ref('');
const campaign = ref(null);
const post = ref(null);
const daily = ref([]);
const dailySales = ref([]);
const adsets = ref([]);
const stock = ref(null);
const analysis = ref(null);
const isAnalyzing = ref(false);

const load = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    const data = await fetchMarketplaceJson(
      route.params.accountId,
      `meta_ads/${route.params.id}`
    );
    campaign.value = data.campaign;
    post.value = data.post;
    daily.value = data.daily || [];
    dailySales.value = data.dailySales || [];
    adsets.value = data.adsets || [];
    stock.value = data.stock;
    analysis.value = data.analysis;
  } catch {
    loadError.value = 'Kampanya verisi yüklenemedi.';
  } finally {
    isLoading.value = false;
  }
};

const runAnalysis = async () => {
  stopSpeaking();
  isAnalyzing.value = true;
  try {
    const data = await postMarketplaceJson(
      route.params.accountId,
      `meta_ads/${route.params.id}/analyze`
    );
    analysis.value = data.analysis;
  } catch {
    // sessizce geç, kullanıcı tekrar deneyebilir
  } finally {
    isAnalyzing.value = false;
  }
};

onMounted(load);

const goBack = () => router.push({ name: 'meta_ads_index' });

const REC_LABELS = {
  continue: { text: 'Devam Et', cls: 'bg-n-teal-3 text-n-teal-11' },
  increase_budget: { text: 'Bütçe Artır', cls: 'bg-n-teal-3 text-n-teal-11' },
  pause: { text: 'Durdur', cls: 'bg-n-ruby-3 text-n-ruby-11' },
  decrease_budget: { text: 'Bütçe Düşür', cls: 'bg-n-amber-3 text-n-amber-11' },
  watch: { text: 'İzle', cls: 'bg-n-amber-3 text-n-amber-11' },
};

// AI analizini sesli okuma için düz metne çevirir (markdown işaretlerini ayıkla).
const cleanForSpeech = t =>
  (t || '').replace(/[*#`_>~]/g, '').replace(/\s*\n\s*/g, '. ');

const buildSpeechText = () => {
  if (!analysis.value) return '';
  const a = analysis.value;
  const rec = (REC_LABELS[a.recommendation] || REC_LABELS.watch).text;
  const parts = [`Öneri, ${rec}.`, a.headline, cleanForSpeech(a.body)];
  if (a.adsetAdvice?.length) {
    parts.push('Reklam seti önerileri.');
    a.adsetAdvice.forEach(adv => parts.push(`${adv.name}. ${adv.action}`));
  }
  return parts.filter(Boolean).join('. ');
};

const formatDate = iso => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = n => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

// Gunluk satis verisini tarihe gore eslestirilebilir bir map'e cevirir, ki
// gunluk reklam tablosunda her satira o gunun Ticimax/Shopify adedini
// ekleyebilelim.
const salesByDate = computed(() => {
  const map = {};
  dailySales.value.forEach(row => {
    map[row.date] = row;
  });
  return map;
});

const dailyRows = computed(() =>
  daily.value.map(row => ({
    ...row,
    ticimax: salesByDate.value[row.date]?.ticimax || 0,
    shopify: salesByDate.value[row.date]?.shopify || 0,
    totalSales: salesByDate.value[row.date]?.total || 0,
  }))
);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center gap-3 px-4 py-3 border-b sm:px-6 sm:py-4 border-n-weak flex-shrink-0"
    >
      <button
        type="button"
        class="flex items-center justify-center rounded-lg size-9 text-n-slate-11 hover:bg-n-alpha-1"
        @click="goBack"
      >
        <span class="size-5 i-lucide-arrow-left" />
      </button>
      <div class="min-w-0">
        <h1 class="text-lg font-medium truncate text-n-slate-12">
          {{ campaign ? campaign.name : 'Kampanya Detayı' }}
        </h1>
        <p v-if="campaign" class="text-sm truncate text-n-slate-11">
          {{ ACCOUNT_LABELS[campaign.accountKey] || campaign.accountKey }} ·
          {{ formatDate(campaign.startTime || campaign.campaignCreatedAt) }}
          →
          {{
            campaign.effectiveStatus === 'ACTIVE'
              ? 'şu an'
              : formatDate(campaign.stopTime)
          }}
        </p>
      </div>
    </header>

    <div class="flex-1 overflow-y-auto">
      <div
        v-if="isLoading"
        class="flex flex-col items-center justify-center h-full gap-3 text-n-slate-11"
      >
        <Spinner />
        <p class="text-sm">Kampanya verisi yükleniyor…</p>
      </div>

      <div
        v-else-if="loadError || !campaign"
        class="flex flex-col items-center justify-center h-full gap-3 text-n-slate-11"
      >
        <span class="size-9 i-lucide-alert-triangle text-n-ruby-9" />
        <p class="text-sm">{{ loadError || 'Kampanya bulunamadı.' }}</p>
      </div>

      <div
        v-else
        class="grid max-w-6xl grid-cols-1 gap-6 px-4 py-5 mx-auto sm:px-6 lg:grid-cols-3"
      >
        <!-- Sol sutun: gercek gonderi + urun eslesmesi -->
        <div class="flex flex-col order-2 gap-6 lg:order-1 lg:col-span-1">
          <section
            class="overflow-hidden border rounded-xl border-n-weak bg-n-solid-1"
          >
            <h3
              class="px-4 pt-4 mb-2 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              Mevcut Gönderi
            </h3>
            <template v-if="post">
              <img
                v-if="post.full_picture"
                :src="post.full_picture"
                class="object-cover w-full aspect-square"
                alt=""
              />
              <div class="p-4">
                <p
                  class="text-sm whitespace-pre-line text-n-slate-12 line-clamp-6"
                >
                  {{ post.message }}
                </p>
                <a
                  v-if="post.permalink_url"
                  :href="post.permalink_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 mt-3 text-xs font-medium text-n-brand hover:underline"
                >
                  <span class="size-3.5 i-lucide-external-link" />
                  Gönderiyi Facebook'ta görüntüle
                </a>
              </div>
            </template>
            <p v-else class="p-4 text-xs text-n-slate-9">
              Gönderi verisi çekilemedi (silinmiş olabilir veya Meta'nın ek izin
              gerektirdiği bir alan olabilir).
            </p>
          </section>

          <section
            v-if="campaign.product"
            class="p-4 border rounded-xl border-n-weak bg-n-solid-1"
          >
            <h3
              class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              Eşleşen Ürün
            </h3>
            <div class="flex items-center gap-3">
              <img
                v-if="campaign.product.image"
                :src="campaign.product.image"
                class="object-cover rounded-lg size-12 flex-shrink-0"
                alt=""
              />
              <div class="min-w-0">
                <p class="text-sm font-medium truncate text-n-slate-12">
                  {{ campaign.product.title }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{ campaign.product.code }}
                </p>
              </div>
            </div>
          </section>

          <!-- Stok dökümü — düşük/tükenmiş bedenler AI'nın "reklamı durdur"
               kararının ana girdisi, kullanıcı da bir bakışta görsün. -->
          <section
            v-if="stock"
            class="p-4 border rounded-xl border-n-weak bg-n-solid-1"
          >
            <h3
              class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              Stok Durumu
            </h3>
            <p class="mb-2 text-sm">
              <span class="font-semibold text-n-slate-12">{{
                stock.totalStock
              }}</span>
              <span class="text-n-slate-10">
                adet ({{ stock.variantCount }} varyant)</span
              >
            </p>
            <div class="flex flex-wrap gap-1.5">
              <span
                v-for="v in stock.variants"
                :key="v.code"
                class="px-2 py-0.5 text-xs rounded-md"
                :class="
                  v.stock === 0
                    ? 'bg-n-ruby-3 text-n-ruby-11'
                    : v.stock <= 3
                      ? 'bg-n-amber-3 text-n-amber-11'
                      : 'bg-n-solid-2 text-n-slate-11'
                "
              >
                {{ v.size }}: {{ v.stock }}
              </span>
            </div>
          </section>

          <section class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
            <h3
              class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              Kampanya Bilgisi
            </h3>
            <dl class="space-y-2 text-sm">
              <div class="flex justify-between gap-2">
                <dt class="text-n-slate-10">Hedef</dt>
                <dd class="text-n-slate-12">{{ campaign.objective }}</dd>
              </div>
              <div class="flex justify-between gap-2">
                <dt class="text-n-slate-10">Durum</dt>
                <dd class="text-n-slate-12">
                  {{
                    campaign.effectiveStatus === 'ACTIVE' ? 'Aktif' : 'Pasif'
                  }}
                </dd>
              </div>
            </dl>
          </section>
        </div>

        <!-- Sag sutun: AI analizi + ozet metrikler + reklam setleri + gun gun tablo -->
        <div class="flex flex-col order-1 gap-6 lg:order-2 lg:col-span-2">
          <!-- AI Reklam Analizi -->
          <section class="p-4 border rounded-xl border-n-brand/40 bg-n-brand/5">
            <div class="flex items-center justify-between gap-3 mb-3">
              <h3
                class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
              >
                <span class="size-4 i-lucide-sparkles text-n-brand" />
                AI Reklam Analizi
              </h3>
              <div class="flex items-center gap-2">
                <button
                  v-if="analysis"
                  type="button"
                  class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg outline outline-1"
                  :class="
                    isSpeaking || isLoadingAudio
                      ? 'bg-n-ruby-9 text-white outline-n-ruby-9'
                      : 'bg-n-teal-9 text-white outline-n-teal-9'
                  "
                  @click="toggleSpeaking(buildSpeechText())"
                >
                  <span
                    class="size-3.5"
                    :class="
                      isLoadingAudio
                        ? 'i-lucide-loader-circle animate-spin'
                        : isSpeaking
                          ? 'i-lucide-square'
                          : 'i-lucide-volume-2'
                    "
                  />
                  {{
                    isLoadingAudio
                      ? 'Ses hazırlanıyor…'
                      : isSpeaking
                        ? 'Durdur'
                        : 'Sesli Dinle'
                  }}
                </button>
                <button
                  type="button"
                  class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-n-brand hover:opacity-90 disabled:opacity-50"
                  :disabled="isAnalyzing"
                  @click="runAnalysis"
                >
                  <span
                    class="size-3.5"
                    :class="
                      isAnalyzing
                        ? 'i-lucide-loader-circle animate-spin'
                        : 'i-lucide-wand-2'
                    "
                  />
                  {{ analysis ? 'Yeniden Analiz Et' : 'Analiz Et' }}
                </button>
              </div>
            </div>

            <div
              v-if="isAnalyzing"
              class="py-6 text-sm text-center text-n-slate-10"
            >
              Tüm veriler okunuyor, AI analiz ediyor… (10-20 saniye)
            </div>

            <div v-else-if="analysis">
              <div class="flex items-center gap-2 mb-2">
                <span
                  class="px-2 py-0.5 text-xs font-semibold rounded-md"
                  :class="
                    (REC_LABELS[analysis.recommendation] || REC_LABELS.watch)
                      .cls
                  "
                >
                  {{
                    (REC_LABELS[analysis.recommendation] || REC_LABELS.watch)
                      .text
                  }}
                </span>
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ analysis.headline }}
                </p>
              </div>
              <p class="text-sm whitespace-pre-line text-n-slate-11">
                {{ analysis.body }}
              </p>
              <div
                v-if="analysis.adsetAdvice && analysis.adsetAdvice.length"
                class="mt-3 space-y-1"
              >
                <p
                  v-for="(advice, i) in analysis.adsetAdvice"
                  :key="i"
                  class="text-xs text-n-slate-10"
                >
                  <span class="font-medium text-n-slate-12"
                    >{{ advice.name }}:</span
                  >
                  {{ advice.action }}
                </p>
              </div>
              <p
                v-if="analysis.createdAt"
                class="mt-2 text-[11px] text-n-slate-9"
              >
                Son analiz: {{ formatDate(analysis.createdAt) }}
              </p>
            </div>

            <p v-else class="py-4 text-sm text-n-slate-10">
              Bu kampanyanın tüm verisini (stok, reklam setleri, satış,
              metrikler) okuyup ne yapmanız gerektiğini söyleyen bir analiz için
              "Analiz Et"e basın.
            </p>
          </section>

          <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div
              class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-xs text-n-slate-10">Toplam Harcama</p>
              <p class="mt-1 text-lg font-semibold text-n-slate-12">
                {{ formatCurrency(campaign.totalSpend) }}
              </p>
            </div>
            <div
              class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-xs text-n-slate-10">Gösterim / Erişim</p>
              <p class="mt-1 text-lg font-semibold text-n-slate-12">
                {{ campaign.totalImpressions }} / {{ campaign.totalReach }}
              </p>
            </div>
            <div
              class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-xs text-n-slate-10">Ort. Sıklık / CPM</p>
              <p class="mt-1 text-lg font-semibold text-n-slate-12">
                {{ campaign.avgFrequency }} /
                {{ formatCurrency(campaign.avgCpm) }}
              </p>
            </div>
            <div
              class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-xs text-n-slate-10">Tıklama</p>
              <p class="mt-1 text-lg font-semibold text-n-slate-12">
                {{ campaign.totalClicks }}
              </p>
            </div>
            <div
              v-if="!campaign.isSalesObjective"
              class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-xs text-n-slate-10">Mesajlaşma</p>
              <p class="mt-1 text-lg font-semibold text-n-slate-12">
                {{ campaign.messagingConversations }}
              </p>
            </div>
            <div
              class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-xs text-n-slate-10">Meta'nın Satın Alma Verisi</p>
              <p class="mt-1 text-lg font-semibold text-n-slate-12">
                {{ campaign.metaPurchases }}
              </p>
            </div>
            <div
              class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-teal-6"
            >
              <p class="text-xs text-n-slate-10">
                Gerçek Satış Adedi (Ticimax+Shopify)
              </p>
              <p class="mt-1 text-lg font-semibold text-n-teal-11">
                {{ campaign.unitsSold != null ? campaign.unitsSold : '—' }}
              </p>
            </div>
          </div>

          <div
            class="flex items-start gap-2 p-3 text-xs rounded-xl bg-n-amber-2 text-n-amber-11 outline outline-1 outline-n-amber-6"
          >
            <span class="flex-shrink-0 mt-0.5 size-4 i-lucide-info" />
            <p>
              Gönderinin beğeni/yorum/paylaşım sayıları bu panelde görünmüyor —
              Meta bunun için "Page Public Content Access" adında ek bir
              uygulama onayı (App Review) istiyor, bu bizim kontrolümüz dışında
              ayrı bir başvuru süreci.
            </p>
          </div>

          <!-- Reklam setleri (Remarketing / Look a Like / Mevcut Hedef Kitle vb.) -->
          <section v-if="adsets.length" class="border rounded-xl border-n-weak">
            <h3
              class="px-4 py-3 text-xs font-semibold tracking-wide uppercase border-b text-n-slate-10 border-n-weak"
            >
              Reklam Setleri
            </h3>
            <div class="overflow-x-auto">
              <table class="w-full text-xs text-left">
                <thead class="bg-n-solid-2 text-n-slate-10">
                  <tr>
                    <th class="px-3 py-2 font-medium">Reklam Seti</th>
                    <th class="px-3 py-2 font-medium">Durum</th>
                    <th class="px-3 py-2 font-medium">Günlük Bütçe</th>
                    <th class="px-3 py-2 font-medium">Harcama</th>
                    <th class="px-3 py-2 font-medium">Tıklama</th>
                    <th class="px-3 py-2 font-medium">Meta Satış</th>
                    <th class="px-3 py-2 font-medium">Satış Başı Maliyet</th>
                    <th class="px-3 py-2 font-medium">Hedefleme</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak">
                  <tr v-for="adset in adsets" :key="adset.id">
                    <td class="px-3 py-2 font-medium text-n-slate-12">
                      {{ adset.name }}
                    </td>
                    <td class="px-3 py-2">
                      <span
                        class="px-1.5 py-0.5 rounded-full"
                        :class="
                          adset.effectiveStatus === 'ACTIVE'
                            ? 'bg-n-teal-3 text-n-teal-11'
                            : 'bg-n-slate-3 text-n-slate-10'
                        "
                      >
                        {{
                          adset.effectiveStatus === 'ACTIVE' ? 'Aktif' : 'Pasif'
                        }}
                      </span>
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap">
                      {{ formatCurrency(adset.dailyBudget) }}
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap">
                      {{ formatCurrency(adset.spend) }}
                    </td>
                    <td class="px-3 py-2">{{ adset.clicks }}</td>
                    <td class="px-3 py-2">{{ adset.metaPurchases }}</td>
                    <td class="px-3 py-2 whitespace-nowrap">
                      {{
                        adset.costPerPurchase != null
                          ? formatCurrency(adset.costPerPurchase)
                          : '—'
                      }}
                    </td>
                    <td class="px-3 py-2 text-n-slate-10">
                      {{ adset.targeting || '—' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="overflow-x-auto border rounded-xl border-n-weak">
            <table class="w-full text-xs text-left">
              <thead class="bg-n-solid-2 text-n-slate-10">
                <tr>
                  <th class="px-3 py-2 font-medium">Tarih</th>
                  <th class="px-3 py-2 font-medium">Harcama</th>
                  <th class="px-3 py-2 font-medium">Gösterim</th>
                  <th class="px-3 py-2 font-medium">Erişim</th>
                  <th class="px-3 py-2 font-medium">Sıklık</th>
                  <th class="px-3 py-2 font-medium">CPM</th>
                  <th class="px-3 py-2 font-medium">Tıklama</th>
                  <th class="px-3 py-2 font-medium">CTR</th>
                  <th
                    v-if="!campaign.isSalesObjective"
                    class="px-3 py-2 font-medium"
                  >
                    Mesaj
                  </th>
                  <th class="px-3 py-2 font-medium">Meta Satış</th>
                  <th class="px-3 py-2 font-medium">Ticimax</th>
                  <th class="px-3 py-2 font-medium">Shopify</th>
                  <th class="px-3 py-2 font-medium">Gerçek Satış</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr v-for="row in dailyRows" :key="row.date">
                  <td class="px-3 py-2 whitespace-nowrap text-n-slate-12">
                    {{ formatDate(row.date) }}
                  </td>
                  <td class="px-3 py-2 whitespace-nowrap">
                    {{ formatCurrency(row.spend) }}
                  </td>
                  <td class="px-3 py-2">{{ row.impressions }}</td>
                  <td class="px-3 py-2">{{ row.reach }}</td>
                  <td class="px-3 py-2">{{ row.frequency }}</td>
                  <td class="px-3 py-2 whitespace-nowrap">
                    {{ formatCurrency(row.cpm) }}
                  </td>
                  <td class="px-3 py-2">{{ row.clicks }}</td>
                  <td class="px-3 py-2">%{{ row.ctr }}</td>
                  <td v-if="!campaign.isSalesObjective" class="px-3 py-2">
                    {{ row.messagingConversations }}
                  </td>
                  <td class="px-3 py-2">{{ row.metaPurchases }}</td>
                  <td class="px-3 py-2">{{ row.ticimax }}</td>
                  <td class="px-3 py-2">{{ row.shopify }}</td>
                  <td class="px-3 py-2 font-medium text-n-teal-11">
                    {{ row.totalSales }}
                  </td>
                </tr>
              </tbody>
            </table>
            <p
              v-if="!dailyRows.length"
              class="p-4 text-xs text-center text-n-slate-10"
            >
              Bu kampanya için henüz günlük veri yok.
            </p>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>
